import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTeacherWeeklySchedule } from '@/hooks/useTeacherWeeklySchedule';
import { OccurrenceRow } from '@/components/teacher/schedule/calendar/OccurrenceRow';
import { supabase } from '@/integrations/supabase/client';
import type { WeeklyItem, StudentInfo, ClassTemplateEvent } from '@/types/schedule';

interface Props {
  teacherCode: string;
  weekStartISO: string;
}

export const WeeklySchedule: React.FC<Props> = ({ teacherCode, weekStartISO }) => {
  const { scheduleQuery } = useTeacherWeeklySchedule(teacherCode, weekStartISO);
  const [projections, setProjections] = useState<WeeklyItem[]>([]);
  const [jobRows, setJobRows] = useState<Array<{ id: string; weekday: number; students?: StudentInfo[] }>>([]);
  const [events, setEvents] = useState<ClassTemplateEvent[]>([]);

  const schedule = scheduleQuery.data?.schedule ?? [];

  const fetchEvents = async () => {
    if (!teacherCode || !weekStartISO) return;
    const endDate = new Date(weekStartISO);
    endDate.setDate(endDate.getDate() + 6);
    const { data } = await (supabase as any)
      .from('v_class_template_events')
      .select('*')
      .eq('requested_by', teacherCode)
      .gte('event_date', weekStartISO)
      .lte('event_date', endDate.toISOString().slice(0, 10));
    setEvents((data ?? []) as ClassTemplateEvent[]);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teacherCode || !weekStartISO) { setProjections([]); return; }
      const start = new Date(weekStartISO);
      const { data: jobs, error } = await (supabase as any)
        .from('v_class_templates')
        .select('id, subject, location, weekday, start_time, end_time, students')
        .eq('teacher_code', teacherCode)
        .eq('is_active', true);
      if (cancelled || error) { setProjections([]); return; }
      setJobRows((jobs ?? []).map((j: any) => ({ id: j.id, weekday: j.weekday, students: j.students ?? [] })));
      const items: WeeklyItem[] = [];
      for (let i = 0; i < 7; i++) {
        const dt = new Date(start);
        dt.setDate(start.getDate() + i);
        const dow = dt.getDay();
        const dateStr = dt.toISOString().slice(0,10);
        for (const j of (jobs ?? []) as any[]) {
          if (j.weekday === dow) {
            items.push({
              fixed_job_id: j.id, occurrence_id: '', date: dateStr,
              subject: j.subject, location: j.location, start_time: j.start_time, end_time: j.end_time,
              duration_minutes: 0, status: 'SCHEDULED', confirmed: false, edit_status: 'NONE',
              insufficient_hours: false, students: j.students, occurrence_edit_requests: [],
            });
          }
        }
      }
      if (!cancelled) setProjections(items);
    })();
    return () => { cancelled = true; };
  }, [teacherCode, weekStartISO]);

  useEffect(() => { fetchEvents(); }, [teacherCode, weekStartISO]);

  const combined = useMemo(() => {
    const jobStudentsMap = new Map<string, StudentInfo[]>();
    for (const j of jobRows) jobStudentsMap.set(j.id, j.students ?? []);
    const scheduleEnriched = (schedule as WeeklyItem[]).map((o) => ({
      ...o, students: o.students ?? jobStudentsMap.get(o.fixed_job_id) ?? [],
    }));
    const occMap = new Map<string, WeeklyItem>();
    for (const o of scheduleEnriched) occMap.set(`${o.date}|${o.fixed_job_id}`, o);
    const seen = new Set<string>();
    const result: WeeklyItem[] = [];
    for (const p of projections) {
      const key = `${p.date}|${p.fixed_job_id}`;
      seen.add(key);
      result.push(occMap.get(key) ?? p);
    }
    for (const o of scheduleEnriched) {
      const key = `${o.date}|${o.fixed_job_id}`;
      if (!seen.has(key)) result.push(o);
    }
    return result;
  }, [projections, schedule, jobRows]);

  const grouped = useMemo(() => {
    const map: Record<string, typeof schedule> = {};
    for (const item of combined) {
      const d = item.date;
      map[d] = map[d] || [];
      map[d].push(item);
    }
    return map;
  }, [combined]);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([date, items]) => (
        <Card key={date}>
          <CardHeader>
            <CardTitle className="text-base">{new Date(date).toLocaleDateString('th-TH')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.sort((a,b)=>a.start_time.localeCompare(b.start_time)).map((item) => (
              <OccurrenceRow
                key={`${item.fixed_job_id}-${item.date}-${item.occurrence_id || 'proj'}`}
                item={item}
                teacherCode={teacherCode}
                events={events}
                onRefresh={fetchEvents}
              />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
